import { useCallback, useEffect, useRef, useState } from 'react';

import { enqueueNotification } from '../../redux/notifications';
import HTTPService, { LambdaResponse } from '../../services/http';

interface RateRecCategory {
  rateRecCategoryId: string;
  rateRecCategoryName: string;
  adjustments?: unknown[];
}

export function useRateRecCategories() {
  const [rateRecCategories, setRateRecCategories] = useState<RateRecCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // To handle memory leak warning
  const isMountedRef = useRef(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await HTTPService.request<LambdaResponse<RateRecCategory[]>>({
        method: 'get',
        relativePath: '/v1/rate-rec-categories'
      });

      if (isMountedRef.current) setRateRecCategories(data);
    } catch (error: unknown) {
      if (isMountedRef.current) {
        console.error(error);
        enqueueNotification({
          message: 'Failed to fetch Rate Rec Categories',
          options: { variant: 'error' }
        });
        setRateRecCategories([]);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [setIsLoading, setRateRecCategories]);

  const updateData = useCallback(
    async (id: string, adjustments: unknown) => {
      try {
        await HTTPService.request({
          method: 'put',
          relativePath: `/v1/rate-rec-categories/${id}/adjustments`,
          data: { rateRecCategoryId: id, adjustments }
        });
        if (isMountedRef.current) await fetchData();
      } catch (error: unknown) {
        if (isMountedRef.current) {
          console.error(error);
          enqueueNotification({
            message: 'Failed to update Rate Rec Category',
            options: { variant: 'error' }
          });
        }
      }
    },
    [fetchData]
  );

  useEffect(() => {
    isMountedRef.current = true;
    void fetchData();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  return { rateRecCategories, isLoading, fetchData, updateData };
}
